import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    inject,
    input
} from '@angular/core';
import { LISTING_UPDATE_EVENT } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { GtmService } from '@df/module-gtm/gtm.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollService } from '@df/util/service/scroll.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { DialogComponent } from '../../../../common/dialog/dialog.component';
import { DIALOG_EVENT, DialogEventType } from '../../../../common/dialog/dialog.interface';
import { DialogOpenDirective } from '../../../../common/dialog/open/dialog-open.directive';
import { ActionComponent } from '../../../../ui/api/action.component';
import { FilterAttributeOptionComponent } from '../filter/attribute-option/filter-attribute-option.component';
import { FilterAttributeRangeComponent } from '../filter/attribute-range/filter-attribute-range.component';
import { FilterStockComponent } from '../filter/stock/filter-stock.component';
import { ProductListingComponent } from '../product-listing.component';
import { OverlayComponent } from './../../../../ui/common/overlay/overlay.component';

@Component({
    selector: 'filters',
    templateUrl: './filters.component.html',
    styleUrl: './filters.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        ActionComponent,
        CommonModule,
        DialogComponent,
        DialogOpenDirective,
        FilterAttributeOptionComponent,
        FilterAttributeRangeComponent,
        FilterStockComponent,
        GridModule,
        OverlayComponent,
        SizeClassDirective,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersComponent extends UiComponent implements OnInit, OnDestroy {
    readonly productListingComponent = input.required<ProductListingComponent>();

    readonly dialogId = 'filters-dialog';

    /**
     * Listing setter
     */
    @Input()
    set listing(listing: ProductListing) {
        this.unsubscribeListing();

        this._listing = listing;

        this.listingSubscription = listing?.subscribe(() => this.detectChanges());
    }
    get listing(): ProductListing {
        return this._listing!;
    }

    @Output()
    filtersOpen = new EventEmitter();

    @Output()
    filtersClose = new EventEmitter();

    protected readonly app = inject(App);
    private dfEventService = inject(DfEventService);
    private scrollService = inject(ScrollService);
    private events = inject(Events);
    private gtmService = inject(GtmService);

    private listingSubscription?: Subscription;
    private scrollReleaseSubscription?: Subscription;
    private listingScrollTimeoutId?: number;
    private reblockScrollTimeoutId?: number;

    /**
     * Listing model
     */
    private _listing?: ProductListing;

    constructor() {
        super();

        this.subscriptions.add(this.events.on('search.open').subscribe(() => this.closeModal()));

        // Listen for dialog events to emit component events
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === DIALOG_EVENT && event.data.id === this.dialogId) {
                    if (event.data.type === DialogEventType.Close) {
                        this.filtersClose.emit();
                    }
                }
            })
        );
    }

    get filters() {
        return this.listing.getFiltersWithManyCurrentMatches();
    }

    openModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Open,
                id: this.dialogId
            },
            action: () => {
                this.sendGtmEvent('openFilters');
                this.filtersOpen.emit();
            }
        });
    }

    closeModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Close,
                id: this.dialogId
            }
        });

        this.sendGtmEvent('closeFilters');
        this.filtersClose.emit();
    }

    afterOpen = (dialog: DialogComponent) => {
        this.scrollReleaseSubscription?.unsubscribe();
        this.scrollReleaseSubscription = this.events.on(LISTING_UPDATE_EVENT).subscribe(() => {
            const currentScrollRelease = dialog?.scrollRelease;

            if (currentScrollRelease && typeof currentScrollRelease === 'function') {
                currentScrollRelease();
                dialog.scrollRelease = undefined;
            }

            this.clearScrollTimeouts();

            if (!this.isDialogActive(dialog)) {
                return;
            }

            this.listingScrollTimeoutId = this.timeoutService.setTimeout(() => {
                if (!this.isDialogActive(dialog)) {
                    return;
                }

                this.returnToListingTop();

                this.reblockScrollTimeoutId = this.timeoutService.setTimeout(() => {
                    if (!this.isDialogActive(dialog) || dialog.scrollRelease) {
                        return;
                    }

                    dialog.scrollRelease = this.scrollService.blockScroll();
                }, 500);
            }, 100);
        });
    };

    afterClose = (dialog: DialogComponent) => {
        this.clearScrollTimeouts();

        // Clean up scroll release if it exists
        if (dialog?.scrollRelease && typeof dialog.scrollRelease === 'function') {
            dialog.scrollRelease();
            dialog.scrollRelease = undefined;
        }

        // Clean up subscription
        this.scrollReleaseSubscription?.unsubscribe();
        this.scrollReleaseSubscription = undefined;
    };

    clearFilters() {
        this.listing.resetFilters();
        this.closeModal();
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    private unsubscribeListing() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
        }
    }

    private returnToListingTop() {
        if (this.app.isPlatformBrowser) {
            const min = [-200, -200, -100, -100];
            const nativeElement = this.productListingComponent().nativeElement;

            if (min !== undefined) {
                // do nothing if top of the listing is in viewport
                if (isInViewport(nativeElement, 100, 'top')) {
                    return;
                }
                const rect = (nativeElement as HTMLElement).getBoundingClientRect();
                const minVal = (Array.isArray(min) ? min[this.client.sizeId] : min) || 0;
                const scrollTop = this.client.getRootElement().scrollTop;

                // typical case - we are below then just scroll to it
                if (rect.top < minVal) {
                    const topOnPage = scrollTop + rect.top;
                    this.client.getRootElement().scrollTop = minVal + topOnPage;
                }
                // just to cover a case when listing is not in view at all then lets jump to it
                else {
                    nativeElement.scrollIntoView(true);
                }
            }
        }
    }

    override ngOnDestroy() {
        this.clearScrollTimeouts();
        this.scrollReleaseSubscription?.unsubscribe();
        this.scrollReleaseSubscription = undefined;
        super.ngOnDestroy();
    }

    private clearScrollTimeouts(): void {
        if (this.listingScrollTimeoutId !== undefined) {
            clearTimeout(this.listingScrollTimeoutId);
            this.listingScrollTimeoutId = undefined;
        }

        if (this.reblockScrollTimeoutId !== undefined) {
            clearTimeout(this.reblockScrollTimeoutId);
            this.reblockScrollTimeoutId = undefined;
        }
    }

    private isDialogActive(dialog: DialogComponent | undefined): dialog is DialogComponent {
        return !!dialog?.isVisible$.value;
    }
}
